import {
  COGO_DOCUMENT_TEMPLATE_FAMILY_SCHEMA_VERSION,
  type CogoDocumentTemplateFamily,
  type CogoDocumentTemplateFamilySlot,
  type CogoDocumentTemplateFamilyTarget,
  type CogoDocumentTemplateFamilyVariant,
} from "@cogo/schema/document-template-family-runtime";
import {
  validateDocumentTemplateFamilyAdmission,
  type CogoDocumentTemplateFamilyAdmissionResult,
} from "@cogo/schema/document-template-family-admission";
import { sha256CanonicalJson } from "@cogo/schema/document-template-runtime";
import {
  COGO_DOCUMENT_TEMPLATE_FAMILY_MODEL_PATCH_POLICY,
  hashCogoDocumentTemplateFamilyPatchImmutableContext,
  hashCogoDocumentTemplateFamilyModelInvocationReceipt,
  type CogoDocumentTemplateFamilyModelInvocationReceipt,
  type CogoDocumentTemplateFamilySlotPatch,
} from "./template-family-model-proposal";
import {
  COGO_DOCUMENT_TEMPLATE_FAMILY_COMPILE_RECEIPT_SCHEMA_VERSION,
  hashCogoDocumentTemplateFamilyCompileReceipt,
  type CogoDocumentTemplateFamilyCompileReceipt,
} from "./template-family-compiler";

export const COGO_DOCUMENT_TEMPLATE_FAMILY_AUTHORING_STATE_SCHEMA_VERSION =
  "cogo.document-template-family-authoring-state.v1" as const;

export const COGO_DOCUMENT_TEMPLATE_FAMILY_PROPOSAL_SCHEMA_VERSION =
  "cogo.document-template-family-proposal.v1" as const;

export const COGO_DOCUMENT_TEMPLATE_FAMILY_TARGET_PROOF_SCHEMA_VERSION =
  "cogo.document-template-family-target-proof.v1" as const;

export const COGO_DOCUMENT_TEMPLATE_FAMILY_TARGET_VERIFICATION_RECEIPT_SCHEMA_VERSION =
  "cogo.document-template-family-target-verification-receipt.v1" as const;

export const COGO_DOCUMENT_TEMPLATE_VISUAL_DIFF_EVIDENCE_SCHEMA_VERSION =
  "cogo.document-template-visual-diff-evidence.v1" as const;

export const COGO_DOCUMENT_TEMPLATE_FAMILY_PROMOTION_SCHEMA_VERSION =
  "cogo.document-template-family-promotion.v1" as const;

export type CogoDocumentTemplateFamilyAuthoringLifecycle =
  | "draft"
  | "candidate"
  | "promoted";

export type CogoDocumentTemplateFamilyProposalActor = "user" | "model";

export type CogoDocumentTemplateFamilyEdit =
  | {
      kind: "set-family-details";
      name?: string;
      description?: string;
      familyVersion?: string;
      ownerRef?: string;
      locale?: string;
      tags?: string[];
    }
  | {
      kind: "upsert-semantic-slot";
      slot: CogoDocumentTemplateFamilySlot;
    }
  | {
      kind: "remove-semantic-slot";
      slotId: string;
    }
  | {
      kind: "upsert-variant";
      variant: CogoDocumentTemplateFamilyVariant;
    }
  | {
      kind: "set-slot-binding";
      variantId: string;
      slotId: string;
      bindingRef: string;
    };

export interface CogoDocumentTemplateFamilyProposal {
  schemaVersion: typeof COGO_DOCUMENT_TEMPLATE_FAMILY_PROPOSAL_SCHEMA_VERSION;
  proposalId: string;
  status: "staged" | "applied" | "rejected";
  baseFamilyRevision: number;
  baseFamilyHash: string;
  edit: CogoDocumentTemplateFamilyEdit;
  affectedSlotIds: string[];
  affectedVariantIds: string[];
  createdBy: CogoDocumentTemplateFamilyProposalActor;
  createdAt: string;
  reason: string;
  modelInvocationReceiptId?: string;
  proposalHash: string;
  requiresHumanApproval: true;
  canonicalAuthority: false;
  finalWriterAllowed: false;
  mayPromote: false;
  decidedAt?: string;
  decidedByRef?: string;
  humanApproved?: true;
}

export interface CogoDocumentTemplateFamilyAdmissionSnapshot {
  familyRevision: number;
  familyHash: string;
  checkedAt: string;
  result: CogoDocumentTemplateFamilyAdmissionResult;
}

export interface CogoDocumentTemplateVisualDiffEvidence {
  schemaVersion: typeof COGO_DOCUMENT_TEMPLATE_VISUAL_DIFF_EVIDENCE_SCHEMA_VERSION;
  renderArtifactRef: string;
  renderArtifactHash: string;
  oracleRef: string;
  oracleHash: string;
  pageCount: number;
  metric: "pixel-mismatch-ratio";
  value: number;
  threshold: number;
  pass: true;
}

export interface CogoDocumentTemplateFamilyTargetVerificationReceipt {
  schemaVersion: typeof COGO_DOCUMENT_TEMPLATE_FAMILY_TARGET_VERIFICATION_RECEIPT_SCHEMA_VERSION;
  verificationId: string;
  verificationAuthority: "cogo-native-writer" | "connected-document-provider";
  verifierRef: string;
  verifiedAt: string;
  compileReceiptHash: string;
  familyId: string;
  familyVersion: string;
  familyRevision: number;
  familyHash: string;
  variantId: string;
  target: CogoDocumentTemplateFamilyTarget;
  generationIrHash: string;
  targetContentType: string;
  artifactRef: string;
  artifactHash: string;
  openabilityReadbackRef: string;
  openabilityReadbackHash: string;
  observedContentType: string;
  structureReadbackRef: string;
  structureHash: string;
  visualDiffEvidence?: CogoDocumentTemplateVisualDiffEvidence;
  providerVersion?: string;
  openabilityReadbackPass: true;
  structureReadbackPass: true;
  canonicalAuthority: false;
  finalWriterAllowed: false;
  receiptHash: string;
}

export type CogoDocumentTemplateFamilyTrustedTargetReceiptVerifier = (
  receipt: CogoDocumentTemplateFamilyTargetVerificationReceipt,
) => Promise<boolean>;

export interface CogoDocumentTemplateFamilyTargetProof {
  schemaVersion: typeof COGO_DOCUMENT_TEMPLATE_FAMILY_TARGET_PROOF_SCHEMA_VERSION;
  proofId: string;
  familyRevision: number;
  familyHash: string;
  variantId: string;
  target: CogoDocumentTemplateFamilyTarget;
  compileReceiptHash: string;
  generationIrHash: string;
  targetContentType: string;
  artifactRef: string;
  artifactHash: string;
  openabilityReadbackRef: string;
  openabilityReadbackHash: string;
  observedContentType: string;
  structureReadbackRef: string;
  structureHash: string;
  visualDiffEvidence?: CogoDocumentTemplateVisualDiffEvidence;
  providerVersion?: string;
  recordedByRef: string;
  recordedAt: string;
  openabilityReadbackPass: true;
  structureReadbackPass: true;
  canonicalAuthority: false;
  finalWriterAllowed: false;
  verificationReceipt: CogoDocumentTemplateFamilyTargetVerificationReceipt;
  proofRecordHash: string;
}

export interface CogoDocumentTemplateFamilyPromotionProofBinding {
  proofId: string;
  proofRecordHash: string;
}

export interface CogoDocumentTemplateFamilyPromotionReceipt {
  schemaVersion: typeof COGO_DOCUMENT_TEMPLATE_FAMILY_PROMOTION_SCHEMA_VERSION;
  promotionId: string;
  promotedAt: string;
  familyId: string;
  familyVersion: string;
  familyRevision: number;
  familyHash: string;
  proofBindings: CogoDocumentTemplateFamilyPromotionProofBinding[];
  admissionSnapshotHash: string;
  reviewerRef: string;
  reviewReason: string;
  humanApproved: true;
  automatedPromotion: false;
  modelCanonicalAuthority: false;
  modelFinalWriterAllowed: false;
  receiptHash: string;
}

export type CogoDocumentTemplateFamilyAuthoringSource =
  | {
      kind: "blank";
      sourceRef: string;
    }
  | {
      kind: "catalog-clone";
      sourceRef: string;
      sourceFamilyId: string;
      sourceFamilyVersion: string;
      sourceFamilyHash: string;
    };

export interface CogoDocumentTemplateFamilyAuthoringState {
  schemaVersion: typeof COGO_DOCUMENT_TEMPLATE_FAMILY_AUTHORING_STATE_SCHEMA_VERSION;
  candidateId: string;
  lifecycle: CogoDocumentTemplateFamilyAuthoringLifecycle;
  authoringRevision: number;
  familyRevision: number;
  familyHash: string;
  family: CogoDocumentTemplateFamily;
  selectedVariantId: string;
  source: CogoDocumentTemplateFamilyAuthoringSource;
  admission: CogoDocumentTemplateFamilyAdmissionSnapshot | null;
  proposals: CogoDocumentTemplateFamilyProposal[];
  modelInvocationReceipts: CogoDocumentTemplateFamilyModelInvocationReceipt[];
  compileReceipts: CogoDocumentTemplateFamilyCompileReceipt[];
  proofs: CogoDocumentTemplateFamilyTargetProof[];
  promotionReceipt: CogoDocumentTemplateFamilyPromotionReceipt | null;
  createdAt: string;
  updatedAt: string;
  canonicalAuthority: false;
  finalWriterAllowed: false;
  modelMayApprove: false;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isPositiveInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value > 0;
}

function isSha256(value: unknown): value is string {
  return typeof value === "string" && /^sha256:[a-f0-9]{64}$/.test(value);
}

function isVisualDiffEvidenceRecord(
  value: unknown,
): value is CogoDocumentTemplateVisualDiffEvidence {
  if (!isRecord(value)) return false;
  const expectedKeys = [
    "metric",
    "oracleHash",
    "oracleRef",
    "pageCount",
    "pass",
    "renderArtifactHash",
    "renderArtifactRef",
    "schemaVersion",
    "threshold",
    "value",
  ].sort();
  return (
    Object.keys(value).sort().join("\u0000") === expectedKeys.join("\u0000") &&
    value.schemaVersion ===
      COGO_DOCUMENT_TEMPLATE_VISUAL_DIFF_EVIDENCE_SCHEMA_VERSION &&
    isNonEmptyString(value.renderArtifactRef) &&
    isSha256(value.renderArtifactHash) &&
    isNonEmptyString(value.oracleRef) &&
    isSha256(value.oracleHash) &&
    isPositiveInteger(value.pageCount) &&
    value.metric === "pixel-mismatch-ratio" &&
    typeof value.value === "number" &&
    Number.isFinite(value.value) &&
    value.value >= 0 &&
    value.value <= 1 &&
    typeof value.threshold === "number" &&
    Number.isFinite(value.threshold) &&
    value.threshold >= 0 &&
    value.threshold <= 1 &&
    value.value <= value.threshold &&
    value.pass === true
  );
}

function hasSafeAuthority(value: Record<string, unknown>): boolean {
  return (
    value.canonicalAuthority === false && value.finalWriterAllowed === false
  );
}

function isTemplateTarget(
  value: unknown,
): value is CogoDocumentTemplateFamilyTarget {
  return [
    "docx",
    "hwpx",
    "pptx",
    "xlsx",
    "pdf",
    "google-docs",
    "notion",
  ].includes(String(value));
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(isNonEmptyString);
}

function isSlotRecord(value: unknown): boolean {
  return (
    isRecord(value) &&
    isNonEmptyString(value.id) &&
    isNonEmptyString(value.kind) &&
    (isNonEmptyString(value.bindingPath) || isNonEmptyString(value.slotRef)) &&
    typeof value.required === "boolean" &&
    isNonEmptyString(value.semanticRole) &&
    (value.bindingPath === undefined || isNonEmptyString(value.bindingPath)) &&
    (value.slotRef === undefined || isNonEmptyString(value.slotRef))
  );
}

function isVariantRecord(value: unknown): boolean {
  return (
    isRecord(value) &&
    isNonEmptyString(value.variantId) &&
    isNonEmptyString(value.name) &&
    ["flow-page", "fixed-page", "workbook", "block-page"].includes(
      String(value.layoutProfile),
    ) &&
    isStringArray(value.knownLimitations) &&
    isRecord(value.layout) &&
    isRecord(value.sourcePolicy) &&
    value.sourcePolicy.adapterCanonicalAuthority === false &&
    Array.isArray(value.slotBindings) &&
    value.slotBindings.every(
      (binding) =>
        isRecord(binding) &&
        isNonEmptyString(binding.slotId) &&
        isNonEmptyString(binding.bindingRef),
    ) &&
    Array.isArray(value.approvedOmissions) &&
    Array.isArray(value.renderTargets) &&
    value.renderTargets.every(isTemplateTarget) &&
    Array.isArray(value.publicationTargets) &&
    value.publicationTargets.every(isTemplateTarget) &&
    Array.isArray(value.targetOverlays) &&
    value.targetOverlays.every(
      (overlay) =>
        isRecord(overlay) &&
        isTemplateTarget(overlay.target) &&
        overlay.canonicalAuthority === false,
    )
  );
}

function isEditRecord(value: unknown): boolean {
  if (!isRecord(value)) return false;
  switch (value.kind) {
    case "set-family-details":
      return (
        ["name", "description", "familyVersion", "ownerRef", "locale"].every(
          (key) => value[key] === undefined || isNonEmptyString(value[key]),
        ) &&
        (value.tags === undefined || isStringArray(value.tags))
      );
    case "upsert-semantic-slot":
      return isSlotRecord(value.slot);
    case "remove-semantic-slot":
      return isNonEmptyString(value.slotId);
    case "upsert-variant":
      return isVariantRecord(value.variant);
    case "set-slot-binding":
      return (
        isNonEmptyString(value.variantId) &&
        isNonEmptyString(value.slotId) &&
        isNonEmptyString(value.bindingRef)
      );
    default:
      return false;
  }
}

function isProposalRecord(value: unknown): boolean {
  const common =
    isRecord(value) &&
    value.schemaVersion ===
      COGO_DOCUMENT_TEMPLATE_FAMILY_PROPOSAL_SCHEMA_VERSION &&
    isNonEmptyString(value.proposalId) &&
    (value.status === "staged" ||
      value.status === "applied" ||
      value.status === "rejected") &&
    isPositiveInteger(value.baseFamilyRevision) &&
    isSha256(value.baseFamilyHash) &&
    isEditRecord(value.edit) &&
    Array.isArray(value.affectedSlotIds) &&
    value.affectedSlotIds.every(isNonEmptyString) &&
    Array.isArray(value.affectedVariantIds) &&
    value.affectedVariantIds.every(isNonEmptyString) &&
    (value.createdBy === "user" || value.createdBy === "model") &&
    isNonEmptyString(value.createdAt) &&
    isNonEmptyString(value.reason) &&
    (value.modelInvocationReceiptId === undefined ||
      isNonEmptyString(value.modelInvocationReceiptId)) &&
    isSha256(value.proposalHash) &&
    value.requiresHumanApproval === true &&
    hasSafeAuthority(value) &&
    value.mayPromote === false;
  if (!common || !isRecord(value)) return false;
  if (value.status === "staged") {
    return (
      value.decidedAt === undefined &&
      value.decidedByRef === undefined &&
      value.humanApproved === undefined
    );
  }
  if (
    !isNonEmptyString(value.decidedAt) ||
    !isNonEmptyString(value.decidedByRef)
  ) {
    return false;
  }
  return value.status === "applied"
    ? value.humanApproved === true
    : value.humanApproved === undefined;
}

function isModelReceiptRecord(value: unknown): boolean {
  return (
    isRecord(value) &&
    value.schemaVersion ===
      "cogo.document-template-family-model-invocation-receipt.v1" &&
    isNonEmptyString(value.receiptId) &&
    value.status === "completed" &&
    value.invocationMode === "live-api" &&
    isNonEmptyString(value.generatedAt) &&
    isNonEmptyString(value.provider) &&
    isNonEmptyString(value.model) &&
    isNonEmptyString(value.promptRef) &&
    isNonEmptyString(value.policyRef) &&
    isNonEmptyString(value.outputRef) &&
    isNonEmptyString(value.familyId) &&
    isNonEmptyString(value.familyVersion) &&
    isNonEmptyString(value.variantId) &&
    isNonEmptyString(value.slotId) &&
    (value.field === "semanticRole" || value.field === "bindingRef") &&
    isPositiveInteger(value.familyRevision) &&
    isSha256(value.familyHash) &&
    isSha256(value.promptHash) &&
    isSha256(value.instructionHash) &&
    isSha256(value.inputContextHash) &&
    isSha256(value.policyHash) &&
    isSha256(value.outputArtifactHash) &&
    isRecord(value.allowedEdit) &&
    value.allowedEdit.scope === "single-slot-field" &&
    (value.allowedEdit.editKind === "upsert-semantic-slot" ||
      value.allowedEdit.editKind === "set-slot-binding") &&
    value.allowedEdit.variantId === value.variantId &&
    value.allowedEdit.slotId === value.slotId &&
    value.allowedEdit.field === value.field &&
    isSha256(value.allowedEdit.immutableContextHash) &&
    value.allowedEdit.outputArtifactHash === value.outputArtifactHash &&
    isSha256(value.receiptHash) &&
    value.requiresHumanReview === true &&
    value.automatedPromotionAllowed === false &&
    value.rawModelOutputStored === false &&
    value.reasoningTraceStored === false &&
    hasSafeAuthority(value)
  );
}

function isCompileReceiptRecord(value: unknown): boolean {
  return (
    isRecord(value) &&
    value.schemaVersion ===
      COGO_DOCUMENT_TEMPLATE_FAMILY_COMPILE_RECEIPT_SCHEMA_VERSION &&
    isNonEmptyString(value.compiledAt) &&
    isNonEmptyString(value.familyId) &&
    isNonEmptyString(value.familyVersion) &&
    isPositiveInteger(value.familyRevision) &&
    isSha256(value.familyHash) &&
    isNonEmptyString(value.variantId) &&
    isSha256(value.generationIrHash) &&
    isNonEmptyString(value.sourceRef) &&
    isSha256(value.sourceHash) &&
    Array.isArray(value.requiredTargets) &&
    value.requiredTargets.every(isNonEmptyString) &&
    value.nativeArtifactStatus === "pending-native-render" &&
    value.modelFinalWriterAllowed === false &&
    isSha256(value.receiptHash)
  );
}

function isVerificationReceiptRecord(value: unknown): boolean {
  return (
    isRecord(value) &&
    value.schemaVersion ===
      COGO_DOCUMENT_TEMPLATE_FAMILY_TARGET_VERIFICATION_RECEIPT_SCHEMA_VERSION &&
    isNonEmptyString(value.verificationId) &&
    (value.verificationAuthority === "cogo-native-writer" ||
      value.verificationAuthority === "connected-document-provider") &&
    isNonEmptyString(value.verifierRef) &&
    isNonEmptyString(value.verifiedAt) &&
    isSha256(value.compileReceiptHash) &&
    isNonEmptyString(value.familyId) &&
    isNonEmptyString(value.familyVersion) &&
    isPositiveInteger(value.familyRevision) &&
    isSha256(value.familyHash) &&
    isNonEmptyString(value.variantId) &&
    isTemplateTarget(value.target) &&
    isSha256(value.generationIrHash) &&
    isNonEmptyString(value.targetContentType) &&
    isNonEmptyString(value.artifactRef) &&
    isSha256(value.artifactHash) &&
    isNonEmptyString(value.openabilityReadbackRef) &&
    isSha256(value.openabilityReadbackHash) &&
    isNonEmptyString(value.observedContentType) &&
    isNonEmptyString(value.structureReadbackRef) &&
    isSha256(value.structureHash) &&
    (value.visualDiffEvidence === undefined ||
      (isVisualDiffEvidenceRecord(value.visualDiffEvidence) &&
        value.visualDiffEvidence.renderArtifactRef !== value.artifactRef &&
        value.visualDiffEvidence.oracleRef !== value.artifactRef &&
        value.visualDiffEvidence.oracleRef !==
          value.visualDiffEvidence.renderArtifactRef)) &&
    value.openabilityReadbackPass === true &&
    value.structureReadbackPass === true &&
    hasSafeAuthority(value) &&
    isSha256(value.receiptHash)
  );
}

function isProofRecord(value: unknown): boolean {
  return (
    isRecord(value) &&
    value.schemaVersion ===
      COGO_DOCUMENT_TEMPLATE_FAMILY_TARGET_PROOF_SCHEMA_VERSION &&
    isNonEmptyString(value.proofId) &&
    isPositiveInteger(value.familyRevision) &&
    isSha256(value.familyHash) &&
    isNonEmptyString(value.variantId) &&
    isTemplateTarget(value.target) &&
    isSha256(value.compileReceiptHash) &&
    isSha256(value.generationIrHash) &&
    isNonEmptyString(value.targetContentType) &&
    isNonEmptyString(value.artifactRef) &&
    isSha256(value.artifactHash) &&
    isNonEmptyString(value.openabilityReadbackRef) &&
    isSha256(value.openabilityReadbackHash) &&
    isNonEmptyString(value.observedContentType) &&
    isNonEmptyString(value.structureReadbackRef) &&
    isSha256(value.structureHash) &&
    (value.visualDiffEvidence === undefined ||
      (isVisualDiffEvidenceRecord(value.visualDiffEvidence) &&
        value.visualDiffEvidence.renderArtifactRef !== value.artifactRef &&
        value.visualDiffEvidence.oracleRef !== value.artifactRef &&
        value.visualDiffEvidence.oracleRef !==
          value.visualDiffEvidence.renderArtifactRef)) &&
    isNonEmptyString(value.recordedByRef) &&
    isNonEmptyString(value.recordedAt) &&
    value.openabilityReadbackPass === true &&
    value.structureReadbackPass === true &&
    hasSafeAuthority(value) &&
    isVerificationReceiptRecord(value.verificationReceipt) &&
    isSha256(value.proofRecordHash)
  );
}

function isPromotionReceiptRecord(value: unknown): boolean {
  return (
    isRecord(value) &&
    value.schemaVersion ===
      COGO_DOCUMENT_TEMPLATE_FAMILY_PROMOTION_SCHEMA_VERSION &&
    isNonEmptyString(value.promotionId) &&
    isNonEmptyString(value.promotedAt) &&
    isNonEmptyString(value.familyId) &&
    isNonEmptyString(value.familyVersion) &&
    isPositiveInteger(value.familyRevision) &&
    isSha256(value.familyHash) &&
    Array.isArray(value.proofBindings) &&
    value.proofBindings.every(
      (binding) =>
        isRecord(binding) &&
        isNonEmptyString(binding.proofId) &&
        isSha256(binding.proofRecordHash),
    ) &&
    isSha256(value.admissionSnapshotHash) &&
    isNonEmptyString(value.reviewerRef) &&
    isNonEmptyString(value.reviewReason) &&
    value.humanApproved === true &&
    value.automatedPromotion === false &&
    value.modelCanonicalAuthority === false &&
    value.modelFinalWriterAllowed === false &&
    isSha256(value.receiptHash)
  );
}

export function isCogoDocumentTemplateFamilyAuthoringState(
  value: unknown,
): value is CogoDocumentTemplateFamilyAuthoringState {
  if (!isRecord(value) || !isRecord(value.family) || !isRecord(value.source)) {
    return false;
  }
  try {
    if (!validateDocumentTemplateFamilyAdmission(value.family).schema.valid) {
      return false;
    }
  } catch {
    return false;
  }
  return (
    value.schemaVersion ===
      COGO_DOCUMENT_TEMPLATE_FAMILY_AUTHORING_STATE_SCHEMA_VERSION &&
    isNonEmptyString(value.candidateId) &&
    (value.lifecycle === "draft" ||
      value.lifecycle === "candidate" ||
      value.lifecycle === "promoted") &&
    isPositiveInteger(value.authoringRevision) &&
    isPositiveInteger(value.familyRevision) &&
    isSha256(value.familyHash) &&
    value.family.schemaVersion ===
      COGO_DOCUMENT_TEMPLATE_FAMILY_SCHEMA_VERSION &&
    isNonEmptyString(value.selectedVariantId) &&
    Array.isArray(value.family.variants) &&
    value.family.variants.some(
      (variant) =>
        isRecord(variant) && variant.variantId === value.selectedVariantId,
    ) &&
    ((value.source.kind === "blank" &&
      isNonEmptyString(value.source.sourceRef)) ||
      (value.source.kind === "catalog-clone" &&
        isNonEmptyString(value.source.sourceRef) &&
        isNonEmptyString(value.source.sourceFamilyId) &&
        isNonEmptyString(value.source.sourceFamilyVersion) &&
        isSha256(value.source.sourceFamilyHash))) &&
    Array.isArray(value.proposals) &&
    value.proposals.every(isProposalRecord) &&
    Array.isArray(value.modelInvocationReceipts) &&
    value.modelInvocationReceipts.every(isModelReceiptRecord) &&
    Array.isArray(value.compileReceipts) &&
    value.compileReceipts.every(isCompileReceiptRecord) &&
    Array.isArray(value.proofs) &&
    value.proofs.every(isProofRecord) &&
    (value.admission === null ||
      (isRecord(value.admission) &&
        isPositiveInteger(value.admission.familyRevision) &&
        isSha256(value.admission.familyHash) &&
        isNonEmptyString(value.admission.checkedAt) &&
        isRecord(value.admission.result) &&
        typeof value.admission.result.admitted === "boolean" &&
        isRecord(value.admission.result.schema) &&
        typeof value.admission.result.schema.valid === "boolean" &&
        Array.isArray(value.admission.result.schema.issues) &&
        isRecord(value.admission.result.semantic) &&
        typeof value.admission.result.semantic.valid === "boolean" &&
        Array.isArray(value.admission.result.semantic.issues))) &&
    (value.promotionReceipt === null ||
      isPromotionReceiptRecord(value.promotionReceipt)) &&
    isNonEmptyString(value.createdAt) &&
    isNonEmptyString(value.updatedAt) &&
    value.canonicalAuthority === false &&
    value.finalWriterAllowed === false &&
    value.modelMayApprove === false
  );
}

export class DocumentTemplateFamilyAuthoringRevisionConflictError extends Error {
  readonly code = "document-template-family-authoring-revision-conflict";

  constructor(
    readonly expectedAuthoringRevision: number,
    readonly actualAuthoringRevision: number,
  ) {
    super(
      `Document template family authoring revision conflict: expected ${expectedAuthoringRevision}, current ${actualAuthoringRevision}.`,
    );
    this.name = "DocumentTemplateFamilyAuthoringRevisionConflictError";
  }
}

export { DocumentTemplateFamilyAuthoringRevisionConflictError as DocumentTemplateFamilyRevisionConflictError };

export class DocumentTemplateFamilyIntegrityError extends Error {
  readonly code = "document-template-family-integrity-error";

  constructor(message: string) {
    super(message);
    this.name = "DocumentTemplateFamilyIntegrityError";
  }
}

function assertNonEmpty(value: string, label: string): void {
  if (!value.trim()) throw new Error(`${label} is required.`);
}

function assertIsoDate(value: string, label: string): void {
  if (Number.isNaN(Date.parse(value))) {
    throw new Error(`${label} must be an ISO date-time.`);
  }
}

function assertSha256(value: string, label: string): void {
  if (!/^sha256:[a-f0-9]{64}$/.test(value)) {
    throw new Error(`${label} must be SHA-256.`);
  }
}

function assertKebabId(value: string, label: string): void {
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value)) {
    throw new Error(`${label} must be kebab-case.`);
  }
}

function assertSemanticVersion(value: string, label: string): void {
  if (!/^\d+\.\d+\.\d+$/.test(value)) {
    throw new Error(`${label} must be semantic version x.y.z.`);
  }
}

function semanticVersionParts(value: string): [number, number, number] {
  assertSemanticVersion(value, "familyVersion");
  return value.split(".").map(Number) as [number, number, number];
}

function compareSemanticVersions(left: string, right: string): number {
  const leftParts = semanticVersionParts(left);
  const rightParts = semanticVersionParts(right);
  for (let index = 0; index < leftParts.length; index += 1) {
    const difference = leftParts[index] - rightParts[index];
    if (difference !== 0) return difference;
  }
  return 0;
}

function nextPatchVersion(value: string): string {
  const [major, minor, patch] = semanticVersionParts(value);
  return `${major}.${minor}.${patch + 1}`;
}

function assertExpectedRevision(
  state: CogoDocumentTemplateFamilyAuthoringState,
  expectedAuthoringRevision: number,
): void {
  if (state.authoringRevision !== expectedAuthoringRevision) {
    throw new DocumentTemplateFamilyAuthoringRevisionConflictError(
      expectedAuthoringRevision,
      state.authoringRevision,
    );
  }
}

function assertFamilyAuthority(family: CogoDocumentTemplateFamily): void {
  if (
    family.semanticCore.llmPolicy.canonicalAuthority !== false ||
    family.semanticCore.llmPolicy.finalWriterAllowed !== false ||
    family.semanticCore.llmPolicy.outputRequiresValidation !== true ||
    family.promotionPolicy.humanPromotionRequired !== true ||
    family.promotionPolicy.automatedPromotionAllowed !== false ||
    family.promotionPolicy.modelMayApprove !== false
  ) {
    throw new DocumentTemplateFamilyIntegrityError(
      "Document template family authority policy must keep the model outside canonical writing and approval.",
    );
  }
  for (const variant of family.variants) {
    if (variant.sourcePolicy.adapterCanonicalAuthority !== false) {
      throw new DocumentTemplateFamilyIntegrityError(
        `Variant ${variant.variantId} assigns canonical authority to an editor adapter.`,
      );
    }
    for (const overlay of variant.targetOverlays) {
      if (overlay.canonicalAuthority !== false) {
        throw new DocumentTemplateFamilyIntegrityError(
          `Variant ${variant.variantId} target ${overlay.target} assigns canonical authority to an adapter.`,
        );
      }
    }
  }
}

function assertStateAuthority(
  state: CogoDocumentTemplateFamilyAuthoringState,
): void {
  if (
    state.canonicalAuthority !== false ||
    state.finalWriterAllowed !== false ||
    state.modelMayApprove !== false
  ) {
    throw new DocumentTemplateFamilyIntegrityError(
      "Template authoring state cannot grant canonical, final-writer, or approval authority to a model.",
    );
  }
  assertFamilyAuthority(state.family);
}

export async function hashCogoDocumentTemplateFamilyTargetVerificationReceipt(
  receipt: Omit<
    CogoDocumentTemplateFamilyTargetVerificationReceipt,
    "receiptHash"
  >,
): Promise<string> {
  return sha256CanonicalJson(receipt);
}

export async function assertCogoDocumentTemplateFamilyAuthoringStateIntegrity(
  state: CogoDocumentTemplateFamilyAuthoringState,
): Promise<void> {
  if (!isCogoDocumentTemplateFamilyAuthoringState(state)) {
    throw new DocumentTemplateFamilyIntegrityError(
      "Document template family authoring state has an invalid or unsafe persisted shape.",
    );
  }
  assertStateAuthority(state);
  const observedHash = await sha256CanonicalJson(state.family);
  if (observedHash !== state.familyHash) {
    throw new DocumentTemplateFamilyIntegrityError(
      "Document template familyHash does not match the canonical family payload.",
    );
  }
  const proposalIds = new Set<string>();
  for (const proposal of state.proposals) {
    if (proposalIds.has(proposal.proposalId)) {
      throw new DocumentTemplateFamilyIntegrityError(
        `Duplicate proposal ID: ${proposal.proposalId}.`,
      );
    }
    proposalIds.add(proposal.proposalId);
    const { proposalHash, ...basis } = proposal;
    if (
      (await sha256CanonicalJson(proposalHashBasis(basis))) !== proposalHash
    ) {
      throw new DocumentTemplateFamilyIntegrityError(
        `Proposal ${proposal.proposalId} does not match its immutable record hash.`,
      );
    }
  }
  const modelReceiptIds = new Set<string>();
  const expectedPolicyHash = await sha256CanonicalJson(
    COGO_DOCUMENT_TEMPLATE_FAMILY_MODEL_PATCH_POLICY,
  );
  for (const receipt of state.modelInvocationReceipts) {
    if (modelReceiptIds.has(receipt.receiptId)) {
      throw new DocumentTemplateFamilyIntegrityError(
        `Duplicate model invocation receipt ID: ${receipt.receiptId}.`,
      );
    }
    modelReceiptIds.add(receipt.receiptId);
    const { receiptHash, ...basis } = receipt;
    if (
      (await hashCogoDocumentTemplateFamilyModelInvocationReceipt(basis)) !==
        receiptHash ||
      receipt.policyHash !== expectedPolicyHash
    ) {
      throw new DocumentTemplateFamilyIntegrityError(
        `Model invocation receipt ${receipt.receiptId} failed immutable hash or policy verification.`,
      );
    }
  }
  for (const proposal of state.proposals) {
    if (proposal.createdBy === "model") {
      const receipt = state.modelInvocationReceipts.find(
        (candidate) =>
          candidate.receiptId === proposal.modelInvocationReceiptId,
      );
      if (!receipt) {
        throw new DocumentTemplateFamilyIntegrityError(
          `Model proposal ${proposal.proposalId} has no matching invocation receipt.`,
        );
      }
      await assertModelReceiptMatchesProposal(receipt, proposal, state.family);
    } else if (proposal.modelInvocationReceiptId !== undefined) {
      throw new DocumentTemplateFamilyIntegrityError(
        `User proposal ${proposal.proposalId} cannot claim a model invocation receipt.`,
      );
    }
  }
  const compileReceiptHashes = new Set<string>();
  for (const receipt of state.compileReceipts) {
    const { receiptHash, ...basis } = receipt;
    if (
      (await hashCogoDocumentTemplateFamilyCompileReceipt(basis)) !==
        receiptHash ||
      receipt.familyId !== state.family.familyId ||
      receipt.familyVersion !== state.family.familyVersion ||
      receipt.familyRevision !== state.familyRevision ||
      receipt.familyHash !== state.familyHash ||
      compileReceiptHashes.has(receiptHash)
    ) {
      throw new DocumentTemplateFamilyIntegrityError(
        `Compile receipt ${receiptHash} failed immutable hash or uniqueness verification.`,
      );
    }
    compileReceiptHashes.add(receiptHash);
  }
  const proofIds = new Set<string>();
  for (const proof of state.proofs) {
    if (proofIds.has(proof.proofId)) {
      throw new DocumentTemplateFamilyIntegrityError(
        `Duplicate target proof ID: ${proof.proofId}.`,
      );
    }
    proofIds.add(proof.proofId);
    const { receiptHash, ...verificationBasis } = proof.verificationReceipt;
    if (
      (await hashCogoDocumentTemplateFamilyTargetVerificationReceipt(
        verificationBasis,
      )) !== receiptHash
    ) {
      throw new DocumentTemplateFamilyIntegrityError(
        `Trusted verification receipt ${proof.verificationReceipt.verificationId} failed immutable hash verification.`,
      );
    }
    const verificationReceipt = proof.verificationReceipt;
    if (
      proof.compileReceiptHash !== verificationReceipt.compileReceiptHash ||
      proof.familyRevision !== verificationReceipt.familyRevision ||
      proof.familyHash !== verificationReceipt.familyHash ||
      proof.variantId !== verificationReceipt.variantId ||
      proof.target !== verificationReceipt.target ||
      proof.generationIrHash !== verificationReceipt.generationIrHash ||
      proof.targetContentType !== verificationReceipt.targetContentType ||
      proof.artifactRef !== verificationReceipt.artifactRef ||
      proof.artifactHash !== verificationReceipt.artifactHash ||
      proof.openabilityReadbackRef !==
        verificationReceipt.openabilityReadbackRef ||
      proof.openabilityReadbackHash !==
        verificationReceipt.openabilityReadbackHash ||
      proof.observedContentType !== verificationReceipt.observedContentType ||
      proof.structureReadbackRef !== verificationReceipt.structureReadbackRef ||
      proof.structureHash !== verificationReceipt.structureHash ||
      (await sha256CanonicalJson(proof.visualDiffEvidence ?? null)) !==
        (await sha256CanonicalJson(
          verificationReceipt.visualDiffEvidence ?? null,
        )) ||
      proof.providerVersion !== verificationReceipt.providerVersion
    ) {
      throw new DocumentTemplateFamilyIntegrityError(
        `Target proof ${proof.proofId} does not exactly mirror its trusted verification receipt.`,
      );
    }
    const { proofRecordHash, ...proofBasis } = proof;
    if ((await sha256CanonicalJson(proofBasis)) !== proofRecordHash) {
      throw new DocumentTemplateFamilyIntegrityError(
        `Target proof ${proof.proofId} does not match its immutable record hash.`,
      );
    }
  }
  if (state.admission) {
    const observedAdmission = validateDocumentTemplateFamilyAdmission(
      state.family,
    );
    if (
      state.admission.familyRevision !== state.familyRevision ||
      state.admission.familyHash !== state.familyHash ||
      (await sha256CanonicalJson(state.admission.result)) !==
        (await sha256CanonicalJson(observedAdmission))
    ) {
      throw new DocumentTemplateFamilyIntegrityError(
        "Stored admission snapshot does not match the current family payload.",
      );
    }
  }
  if ((state.lifecycle === "promoted") !== Boolean(state.promotionReceipt)) {
    throw new DocumentTemplateFamilyIntegrityError(
      "Promoted lifecycle and promotion receipt must be present together.",
    );
  }
  if (state.promotionReceipt) {
    if (!state.admission) {
      throw new DocumentTemplateFamilyIntegrityError(
        "Promotion receipt requires its bound admission snapshot.",
      );
    }
    const { receiptHash, ...promotionBasis } = state.promotionReceipt;
    const expectedBindings = state.proofs
      .map(({ proofId, proofRecordHash }) => ({ proofId, proofRecordHash }))
      .sort((left, right) => left.proofId.localeCompare(right.proofId));
    if (
      (await sha256CanonicalJson(promotionBasis)) !== receiptHash ||
      (await sha256CanonicalJson(state.promotionReceipt.proofBindings)) !==
        (await sha256CanonicalJson(expectedBindings)) ||
      state.promotionReceipt.admissionSnapshotHash !==
        (await sha256CanonicalJson(state.admission))
    ) {
      throw new DocumentTemplateFamilyIntegrityError(
        "Promotion receipt does not match its immutable admission and proof bindings.",
      );
    }
  }
}

const assertStateIntegrity =
  assertCogoDocumentTemplateFamilyAuthoringStateIntegrity;

function assertSelectedVariant(
  family: CogoDocumentTemplateFamily,
  variantId: string,
): void {
  if (!family.variants.some((variant) => variant.variantId === variantId)) {
    throw new Error(`Unknown document template family variant: ${variantId}`);
  }
}

function uniqueStrings(values: string[]): string[] {
  return Array.from(new Set(values)).sort();
}

function proposalEffects(
  family: CogoDocumentTemplateFamily,
  edit: CogoDocumentTemplateFamilyEdit,
): { affectedSlotIds: string[]; affectedVariantIds: string[] } {
  switch (edit.kind) {
    case "set-family-details":
      return { affectedSlotIds: [], affectedVariantIds: [] };
    case "upsert-semantic-slot":
      return { affectedSlotIds: [edit.slot.id], affectedVariantIds: [] };
    case "remove-semantic-slot":
      return {
        affectedSlotIds: [edit.slotId],
        affectedVariantIds: family.variants
          .filter(
            (variant) =>
              variant.slotBindings.some(
                (binding) => binding.slotId === edit.slotId,
              ) ||
              variant.approvedOmissions.some(
                (omission) => omission.slotId === edit.slotId,
              ),
          )
          .map((variant) => variant.variantId),
      };
    case "upsert-variant":
      return {
        affectedSlotIds: edit.variant.slotBindings.map(
          (binding) => binding.slotId,
        ),
        affectedVariantIds: [edit.variant.variantId],
      };
    case "set-slot-binding":
      return {
        affectedSlotIds: [edit.slotId],
        affectedVariantIds: [edit.variantId],
      };
  }
}

function proposalHashBasis(
  proposal: Omit<CogoDocumentTemplateFamilyProposal, "proposalHash">,
): Omit<CogoDocumentTemplateFamilyProposal, "proposalHash"> {
  return structuredClone(proposal);
}

function updateProposal(
  proposals: CogoDocumentTemplateFamilyProposal[],
  next: CogoDocumentTemplateFamilyProposal,
): CogoDocumentTemplateFamilyProposal[] {
  return proposals.map((proposal) =>
    proposal.proposalId === next.proposalId ? next : proposal,
  );
}

function proposalForState(
  state: CogoDocumentTemplateFamilyAuthoringState,
  proposalId: string,
): CogoDocumentTemplateFamilyProposal {
  const proposal = state.proposals.find(
    (candidate) => candidate.proposalId === proposalId,
  );
  if (!proposal) {
    throw new Error(
      `Document template family proposal not found: ${proposalId}`,
    );
  }
  return proposal;
}

async function assertModelReceiptMatchesProposal(
  receipt: CogoDocumentTemplateFamilyModelInvocationReceipt,
  proposal: CogoDocumentTemplateFamilyProposal,
  family: CogoDocumentTemplateFamily,
): Promise<void> {
  if (
    receipt.receiptId !== proposal.modelInvocationReceiptId ||
    receipt.receiptId !== `${proposal.proposalId}.invocation` ||
    receipt.familyId !== family.familyId ||
    receipt.familyRevision !== proposal.baseFamilyRevision ||
    receipt.familyHash !== proposal.baseFamilyHash ||
    receipt.allowedEdit.scope !== "single-slot-field" ||
    receipt.allowedEdit.variantId !== receipt.variantId ||
    receipt.allowedEdit.slotId !== receipt.slotId ||
    receipt.allowedEdit.field !== receipt.field ||
    !isSha256(receipt.allowedEdit.immutableContextHash) ||
    receipt.allowedEdit.outputArtifactHash !== receipt.outputArtifactHash ||
    !family.variants.some((variant) => variant.variantId === receipt.variantId)
  ) {
    throw new DocumentTemplateFamilyIntegrityError(
      `Model receipt ${receipt.receiptId} is not bound to proposal ${proposal.proposalId}'s exact family, variant, slot, and field scope.`,
    );
  }

  let patch: CogoDocumentTemplateFamilySlotPatch;
  if (receipt.field === "semanticRole") {
    if (
      receipt.allowedEdit.editKind !== "upsert-semantic-slot" ||
      proposal.edit.kind !== "upsert-semantic-slot" ||
      proposal.edit.slot.id !== receipt.slotId
    ) {
      throw new DocumentTemplateFamilyIntegrityError(
        `Model receipt ${receipt.receiptId} does not allow this semantic-slot edit shape.`,
      );
    }
    const currentSlot = family.semanticCore.slots.find(
      (slot) => slot.id === receipt.slotId,
    );
    if (
      !currentSlot ||
      (await hashCogoDocumentTemplateFamilyPatchImmutableContext({
        field: "semanticRole",
        slot: currentSlot,
      })) !== receipt.allowedEdit.immutableContextHash ||
      (await hashCogoDocumentTemplateFamilyPatchImmutableContext({
        field: "semanticRole",
        slot: proposal.edit.slot,
      })) !== receipt.allowedEdit.immutableContextHash
    ) {
      throw new DocumentTemplateFamilyIntegrityError(
        `Model proposal ${proposal.proposalId} changes more than the permitted semanticRole field.`,
      );
    }
    patch = {
      slotId: receipt.slotId,
      field: "semanticRole",
      value: proposal.edit.slot.semanticRole,
      reason: proposal.reason,
    };
  } else {
    if (
      receipt.allowedEdit.editKind !== "set-slot-binding" ||
      proposal.edit.kind !== "set-slot-binding" ||
      proposal.edit.variantId !== receipt.variantId ||
      proposal.edit.slotId !== receipt.slotId
    ) {
      throw new DocumentTemplateFamilyIntegrityError(
        `Model receipt ${receipt.receiptId} does not allow this slot-binding edit shape.`,
      );
    }
    const currentVariant = family.variants.find(
      (variant) => variant.variantId === receipt.variantId,
    );
    const currentSlot = family.semanticCore.slots.find(
      (slot) => slot.id === receipt.slotId,
    );
    const currentBinding = currentVariant?.slotBindings.find(
      (binding) => binding.slotId === receipt.slotId,
    );
    if (
      !currentSlot ||
      !currentBinding ||
      (await hashCogoDocumentTemplateFamilyPatchImmutableContext({
        field: "bindingRef",
        slot: currentSlot,
        binding: currentBinding,
      })) !== receipt.allowedEdit.immutableContextHash
    ) {
      throw new DocumentTemplateFamilyIntegrityError(
        `Model proposal ${proposal.proposalId} changes more than the permitted bindingRef field.`,
      );
    }
    patch = {
      slotId: receipt.slotId,
      field: "bindingRef",
      value: proposal.edit.bindingRef,
      reason: proposal.reason,
    };
  }
  if ((await sha256CanonicalJson(patch)) !== receipt.outputArtifactHash) {
    throw new DocumentTemplateFamilyIntegrityError(
      `Model proposal ${proposal.proposalId} does not match its invocation output artifact hash.`,
    );
  }
}

function applyFamilyEdit(
  family: CogoDocumentTemplateFamily,
  edit: CogoDocumentTemplateFamilyEdit,
): CogoDocumentTemplateFamily {
  const next = structuredClone(family);
  switch (edit.kind) {
    case "set-family-details": {
      if (
        edit.name === undefined &&
        edit.description === undefined &&
        edit.familyVersion === undefined &&
        edit.ownerRef === undefined &&
        edit.locale === undefined &&
        edit.tags === undefined
      ) {
        throw new Error("set-family-details must change at least one field.");
      }
      if (edit.name !== undefined) {
        assertNonEmpty(edit.name, "family name");
        next.name = edit.name.trim();
      }
      if (edit.description !== undefined) {
        assertNonEmpty(edit.description, "family description");
        next.description = edit.description.trim();
      }
      if (edit.familyVersion !== undefined) {
        assertSemanticVersion(edit.familyVersion, "familyVersion");
        next.familyVersion = edit.familyVersion;
      }
      if (edit.ownerRef !== undefined) {
        assertNonEmpty(edit.ownerRef, "ownerRef");
        next.metadata.ownerRef = edit.ownerRef.trim();
      }
      if (edit.locale !== undefined) {
        assertNonEmpty(edit.locale, "locale");
        next.metadata.locale = edit.locale.trim();
      }
      if (edit.tags !== undefined) {
        next.metadata.tags = uniqueStrings(edit.tags.map((tag) => tag.trim()));
      }
      return next;
    }
    case "upsert-semantic-slot": {
      assertKebabId(edit.slot.id, "semantic slot id");
      const index = next.semanticCore.slots.findIndex(
        (slot) => slot.id === edit.slot.id,
      );
      if (index === -1) {
        next.semanticCore.slots.push(structuredClone(edit.slot));
      } else {
        next.semanticCore.slots[index] = structuredClone(edit.slot);
      }
      return next;
    }
    case "remove-semantic-slot": {
      if (!next.semanticCore.slots.some((slot) => slot.id === edit.slotId)) {
        throw new Error(`Semantic slot not found: ${edit.slotId}`);
      }
      next.semanticCore.slots = next.semanticCore.slots.filter(
        (slot) => slot.id !== edit.slotId,
      );
      next.variants = next.variants.map((variant) => ({
        ...variant,
        slotBindings: variant.slotBindings.filter(
          (binding) => binding.slotId !== edit.slotId,
        ),
        approvedOmissions: variant.approvedOmissions.filter(
          (omission) => omission.slotId !== edit.slotId,
        ),
      })) as CogoDocumentTemplateFamilyVariant[];
      return next;
    }
    case "upsert-variant": {
      assertKebabId(edit.variant.variantId, "variantId");
      const index = next.variants.findIndex(
        (variant) => variant.variantId === edit.variant.variantId,
      );
      if (index === -1) {
        next.variants.push(structuredClone(edit.variant));
      } else {
        next.variants[index] = structuredClone(edit.variant);
      }
      return next;
    }
    case "set-slot-binding": {
      assertNonEmpty(edit.bindingRef, "bindingRef");
      if (!next.semanticCore.slots.some((slot) => slot.id === edit.slotId)) {
        throw new Error(`Semantic slot not found: ${edit.slotId}`);
      }
      const variant = next.variants.find(
        (candidate) => candidate.variantId === edit.variantId,
      );
      if (!variant) throw new Error(`Variant not found: ${edit.variantId}`);
      const binding = {
        slotId: edit.slotId,
        bindingRef: edit.bindingRef.trim(),
      };
      const bindingIndex = variant.slotBindings.findIndex(
        (candidate) => candidate.slotId === edit.slotId,
      );
      if (bindingIndex === -1) variant.slotBindings.push(binding);
      else variant.slotBindings[bindingIndex] = binding;
      variant.approvedOmissions = variant.approvedOmissions.filter(
        (omission) => omission.slotId !== edit.slotId,
      );
      return next;
    }
  }
}

function makeBlankFamily(input: {
  familyId: string;
  familyVersion: string;
  name: string;
  description: string;
  ownerRef: string;
  locale: string;
}): CogoDocumentTemplateFamily {
  const sourceRef = `workspace://document-template/${input.familyId}/blank`;
  return {
    schemaVersion: COGO_DOCUMENT_TEMPLATE_FAMILY_SCHEMA_VERSION,
    familyId: input.familyId,
    familyVersion: input.familyVersion,
    name: input.name,
    description: input.description,
    metadata: {
      ownerRef: input.ownerRef,
      locale: input.locale,
      tags: ["browser-authored", "flow-page"],
    },
    legacyTemplateRefs: [],
    semanticCore: {
      dataSchema: {
        ref: `workspace://document-template/${input.familyId}/input-schema`,
      },
      slots: [
        {
          id: "primary-content",
          kind: "rich-text",
          bindingPath: "/primaryContent",
          required: true,
          semanticRole: "primary-content",
        },
      ],
      tokens: {
        designTokenRefs: ["token-set://cogo/document/default"],
      },
      assets: [],
      guidelineRefs: [
        {
          ref: "guideline://cogo/document-template-quality/v2",
          required: true,
        },
      ],
      llmPolicy: {
        allowedRoles: [
          "semantic-input-assistant",
          "layout-proposal-assistant",
          "content-revision-assistant",
        ],
        canonicalAuthority: false,
        finalWriterAllowed: false,
        outputRequiresValidation: true,
      },
      actionFlowRefs: [
        {
          id: "generate-document",
          ref: "actionflow://document-template/generate/v1",
          role: "generation",
        },
        {
          id: "approve-document",
          ref: "actionflow://document-template/approve/v1",
          role: "approval",
        },
      ],
      proofProfile: {
        requiredForPromotion: true,
        nativeStructureReadbackRequired: true,
        targetReadbackRequired: true,
        structureHashRequired: true,
        providerVersionPolicy: "required-when-provider",
        visualDiffPolicy: "when-layout-bearing",
        evidenceRetention: "immutable",
      },
      sourceProvenance: {
        required: true,
        sourceRefs: [sourceRef],
        preserveProviderVersion: true,
        sourceAdaptersCanonical: false,
      },
    },
    promotionPolicy: {
      humanPromotionRequired: true,
      automatedPromotionAllowed: false,
      modelMayApprove: false,
      evidenceRequired: true,
    },
    variants: [
      {
        variantId: "flow-page-default",
        name: "Flow Page",
        layoutProfile: "flow-page",
        capabilityStatus: "partial",
        knownLimitations: [
          "Native DOCX and PDF files must be written and checked before promotion.",
        ],
        layout: {
          kind: "flow-page",
          layoutRef: `workspace://document-template/${input.familyId}/layout/flow-page`,
          pagination: "flow",
        },
        sourcePolicy: {
          canonicalModel: "cogo-document-template-family",
          editorAdapters: ["wordgard"],
          adapterCanonicalAuthority: false,
        },
        slotBindings: [
          {
            slotId: "primary-content",
            bindingRef: `workspace://document-template/${input.familyId}/bindings/primary-content`,
          },
        ],
        approvedOmissions: [],
        renderTargets: ["docx", "pdf"],
        publicationTargets: [],
        targetOverlays: [
          {
            target: "docx",
            adapter: "cogo-document-renderer:docx",
            canonicalAuthority: false,
            implementationStatus: "partial",
            readbackRequired: true,
            templateMode: "generated-from-cogo",
          },
          {
            target: "pdf",
            adapter: "cogo-document-renderer:pdf",
            canonicalAuthority: false,
            implementationStatus: "partial",
            readbackRequired: true,
            outputOnly: true,
            editable: false,
          },
        ],
      },
    ],
  };
}

async function makeInitialState(input: {
  candidateId: string;
  family: CogoDocumentTemplateFamily;
  selectedVariantId: string;
  source: CogoDocumentTemplateFamilyAuthoringSource;
  createdAt: string;
}): Promise<CogoDocumentTemplateFamilyAuthoringState> {
  assertNonEmpty(input.candidateId, "candidateId");
  assertIsoDate(input.createdAt, "createdAt");
  assertFamilyAuthority(input.family);
  assertSelectedVariant(input.family, input.selectedVariantId);
  const familyHash = await sha256CanonicalJson(input.family);
  return {
    schemaVersion: COGO_DOCUMENT_TEMPLATE_FAMILY_AUTHORING_STATE_SCHEMA_VERSION,
    candidateId: input.candidateId,
    lifecycle: "draft",
    authoringRevision: 1,
    familyRevision: 1,
    familyHash,
    family: structuredClone(input.family),
    selectedVariantId: input.selectedVariantId,
    source: structuredClone(input.source),
    admission: null,
    proposals: [],
    modelInvocationReceipts: [],
    compileReceipts: [],
    proofs: [],
    promotionReceipt: null,
    createdAt: input.createdAt,
    updatedAt: input.createdAt,
    canonicalAuthority: false,
    finalWriterAllowed: false,
    modelMayApprove: false,
  };
}

export async function createBlankCogoDocumentTemplateFamilyAuthoringState(input: {
  candidateId: string;
  familyId: string;
  familyVersion?: string;
  name: string;
  description?: string;
  ownerRef: string;
  locale?: string;
  createdAt: string;
}): Promise<CogoDocumentTemplateFamilyAuthoringState> {
  assertKebabId(input.familyId, "familyId");
  const familyVersion = input.familyVersion ?? "0.1.0";
  assertSemanticVersion(familyVersion, "familyVersion");
  assertNonEmpty(input.name, "family name");
  assertNonEmpty(input.ownerRef, "ownerRef");
  const family = makeBlankFamily({
    familyId: input.familyId,
    familyVersion,
    name: input.name.trim(),
    description:
      input.description?.trim() ||
      `Browser-authored flow-page template family for ${input.name.trim()}.`,
    ownerRef: input.ownerRef.trim(),
    locale: input.locale ?? "ko-KR",
  });
  const admission = validateDocumentTemplateFamilyAdmission(family);
  if (!admission.admitted) {
    throw new DocumentTemplateFamilyIntegrityError(
      "The built-in blank document template family no longer passes combined admission.",
    );
  }
  return makeInitialState({
    candidateId: input.candidateId,
    family,
    selectedVariantId: family.variants[0].variantId,
    source: {
      kind: "blank",
      sourceRef: `workspace://document-template/${input.familyId}/blank`,
    },
    createdAt: input.createdAt,
  });
}

export async function cloneCatalogCogoDocumentTemplateFamilyAuthoringState(input: {
  candidateId: string;
  sourceFamily: CogoDocumentTemplateFamily;
  familyId: string;
  familyVersion: string;
  name?: string;
  ownerRef?: string;
  selectedVariantId?: string;
  createdAt: string;
}): Promise<CogoDocumentTemplateFamilyAuthoringState> {
  const sourceAdmission = validateDocumentTemplateFamilyAdmission(
    input.sourceFamily,
  );
  if (!sourceAdmission.admitted) {
    throw new Error(
      "Catalog source family must pass combined admission before cloning.",
    );
  }
  assertKebabId(input.familyId, "familyId");
  assertSemanticVersion(input.familyVersion, "familyVersion");
  const sourceFamilyHash = await sha256CanonicalJson(input.sourceFamily);
  const sourceRef = `catalog://${input.sourceFamily.familyId}/${input.sourceFamily.familyVersion}`;
  const family = structuredClone(input.sourceFamily);
  family.familyId = input.familyId;
  family.familyVersion = input.familyVersion;
  family.name = input.name?.trim() || `${input.sourceFamily.name} Copy`;
  if (input.ownerRef !== undefined) {
    assertNonEmpty(input.ownerRef, "ownerRef");
    family.metadata.ownerRef = input.ownerRef.trim();
  }
  family.semanticCore.sourceProvenance.sourceRefs = uniqueStrings([
    ...family.semanticCore.sourceProvenance.sourceRefs,
    sourceRef,
  ]);
  const selectedVariantId =
    input.selectedVariantId ?? family.variants[0]?.variantId ?? "";
  const candidateAdmission = validateDocumentTemplateFamilyAdmission(family);
  if (!candidateAdmission.admitted) {
    throw new DocumentTemplateFamilyIntegrityError(
      "Catalog-cloned document template family no longer passes combined admission.",
    );
  }
  return makeInitialState({
    candidateId: input.candidateId,
    family,
    selectedVariantId,
    source: {
      kind: "catalog-clone",
      sourceRef,
      sourceFamilyId: input.sourceFamily.familyId,
      sourceFamilyVersion: input.sourceFamily.familyVersion,
      sourceFamilyHash,
    },
    createdAt: input.createdAt,
  });
}

export async function selectCogoDocumentTemplateFamilyVariant(input: {
  state: CogoDocumentTemplateFamilyAuthoringState;
  expectedAuthoringRevision: number;
  variantId: string;
  selectedAt: string;
}): Promise<CogoDocumentTemplateFamilyAuthoringState> {
  assertExpectedRevision(input.state, input.expectedAuthoringRevision);
  assertIsoDate(input.selectedAt, "selectedAt");
  await assertStateIntegrity(input.state);
  assertSelectedVariant(input.state.family, input.variantId);
  return {
    ...input.state,
    authoringRevision: input.state.authoringRevision + 1,
    selectedVariantId: input.variantId,
    updatedAt: input.selectedAt,
  };
}

export async function recordCogoDocumentTemplateFamilyModelInvocation(input: {
  state: CogoDocumentTemplateFamilyAuthoringState;
  expectedAuthoringRevision: number;
  receipt: CogoDocumentTemplateFamilyModelInvocationReceipt;
}): Promise<CogoDocumentTemplateFamilyAuthoringState> {
  assertExpectedRevision(input.state, input.expectedAuthoringRevision);
  await assertStateIntegrity(input.state);
  const receipt = input.receipt;
  if (
    receipt.familyId !== input.state.family.familyId ||
    receipt.familyVersion !== input.state.family.familyVersion ||
    receipt.familyRevision !== input.state.familyRevision ||
    receipt.familyHash !== input.state.familyHash ||
    !input.state.family.variants.some(
      (variant) => variant.variantId === receipt.variantId,
    )
  ) {
    throw new DocumentTemplateFamilyIntegrityError(
      "Model invocation receipt does not match the current family revision and variant.",
    );
  }
  if (
    receipt.canonicalAuthority !== false ||
    receipt.finalWriterAllowed !== false ||
    receipt.requiresHumanReview !== true ||
    receipt.automatedPromotionAllowed !== false
  ) {
    throw new DocumentTemplateFamilyIntegrityError(
      "Model invocation receipt violates the template authoring authority policy.",
    );
  }
  const { receiptHash, ...receiptBasis } = receipt;
  if (
    (await hashCogoDocumentTemplateFamilyModelInvocationReceipt(
      receiptBasis,
    )) !== receiptHash ||
    receipt.policyHash !==
      (await sha256CanonicalJson(
        COGO_DOCUMENT_TEMPLATE_FAMILY_MODEL_PATCH_POLICY,
      )) ||
    receipt.allowedEdit.scope !== "single-slot-field" ||
    receipt.allowedEdit.variantId !== receipt.variantId ||
    receipt.allowedEdit.slotId !== receipt.slotId ||
    receipt.allowedEdit.field !== receipt.field ||
    !isSha256(receipt.allowedEdit.immutableContextHash) ||
    receipt.allowedEdit.outputArtifactHash !== receipt.outputArtifactHash
  ) {
    throw new DocumentTemplateFamilyIntegrityError(
      "Model invocation receipt failed immutable hash, policy, or allowed-edit binding verification.",
    );
  }
  if (
    input.state.modelInvocationReceipts.some(
      (candidate) => candidate.receiptId === receipt.receiptId,
    )
  ) {
    throw new Error(`Duplicate model invocation receipt: ${receipt.receiptId}`);
  }
  return {
    ...input.state,
    authoringRevision: input.state.authoringRevision + 1,
    modelInvocationReceipts: [
      ...input.state.modelInvocationReceipts,
      structuredClone(receipt),
    ],
    updatedAt: receipt.generatedAt,
  };
}

export async function stageCogoDocumentTemplateFamilyProposal(input: {
  state: CogoDocumentTemplateFamilyAuthoringState;
  expectedAuthoringRevision: number;
  proposalId: string;
  edit: CogoDocumentTemplateFamilyEdit;
  createdBy: CogoDocumentTemplateFamilyProposalActor;
  modelInvocationReceiptId?: string;
  reason: string;
  createdAt: string;
}): Promise<CogoDocumentTemplateFamilyAuthoringState> {
  assertExpectedRevision(input.state, input.expectedAuthoringRevision);
  await assertStateIntegrity(input.state);
  assertNonEmpty(input.proposalId, "proposalId");
  assertNonEmpty(input.reason, "proposal reason");
  assertIsoDate(input.createdAt, "createdAt");
  if (
    input.state.proposals.some(
      (proposal) => proposal.proposalId === input.proposalId,
    )
  ) {
    throw new Error(
      `Duplicate document template family proposal: ${input.proposalId}`,
    );
  }
  let modelInvocationReceiptId: string | undefined;
  if (input.createdBy === "model") {
    modelInvocationReceiptId = input.modelInvocationReceiptId?.trim();
    const receipt = input.state.modelInvocationReceipts.find(
      (candidate) => candidate.receiptId === modelInvocationReceiptId,
    );
    if (
      !receipt ||
      receipt.receiptId !== `${input.proposalId}.invocation` ||
      receipt.familyRevision !== input.state.familyRevision ||
      receipt.familyHash !== input.state.familyHash
    ) {
      throw new DocumentTemplateFamilyIntegrityError(
        "A model proposal requires its matching current-revision invocation receipt.",
      );
    }
  } else if (input.modelInvocationReceiptId !== undefined) {
    throw new DocumentTemplateFamilyIntegrityError(
      "A user proposal cannot claim a model invocation receipt.",
    );
  }
  const effects = proposalEffects(input.state.family, input.edit);
  const basis: Omit<CogoDocumentTemplateFamilyProposal, "proposalHash"> = {
    schemaVersion: COGO_DOCUMENT_TEMPLATE_FAMILY_PROPOSAL_SCHEMA_VERSION,
    proposalId: input.proposalId,
    status: "staged",
    baseFamilyRevision: input.state.familyRevision,
    baseFamilyHash: input.state.familyHash,
    edit: structuredClone(input.edit),
    affectedSlotIds: uniqueStrings(effects.affectedSlotIds),
    affectedVariantIds: uniqueStrings(effects.affectedVariantIds),
    createdBy: input.createdBy,
    createdAt: input.createdAt,
    reason: input.reason.trim(),
    ...(modelInvocationReceiptId ? { modelInvocationReceiptId } : {}),
    requiresHumanApproval: true,
    canonicalAuthority: false,
    finalWriterAllowed: false,
    mayPromote: false,
  };
  const proposal: CogoDocumentTemplateFamilyProposal = {
    ...basis,
    proposalHash: await sha256CanonicalJson(proposalHashBasis(basis)),
  };
  if (proposal.createdBy === "model") {
    const receipt = input.state.modelInvocationReceipts.find(
      (candidate) => candidate.receiptId === proposal.modelInvocationReceiptId,
    );
    if (!receipt) {
      throw new DocumentTemplateFamilyIntegrityError(
        "Model proposal invocation receipt disappeared before staging.",
      );
    }
    await assertModelReceiptMatchesProposal(
      receipt,
      proposal,
      input.state.family,
    );
  }
  return {
    ...input.state,
    authoringRevision: input.state.authoringRevision + 1,
    proposals: [...input.state.proposals, proposal],
    updatedAt: input.createdAt,
  };
}

export async function stageCogoDocumentTemplateFamilyModelProposalAtomically(input: {
  state: CogoDocumentTemplateFamilyAuthoringState;
  expectedAuthoringRevision: number;
  receipt: CogoDocumentTemplateFamilyModelInvocationReceipt;
  proposalId: string;
  edit: CogoDocumentTemplateFamilyEdit;
  reason: string;
  createdAt: string;
}): Promise<CogoDocumentTemplateFamilyAuthoringState> {
  assertExpectedRevision(input.state, input.expectedAuthoringRevision);
  const stateWithReceipt =
    await recordCogoDocumentTemplateFamilyModelInvocation({
      state: input.state,
      expectedAuthoringRevision: input.expectedAuthoringRevision,
      receipt: input.receipt,
    });
  const staged = await stageCogoDocumentTemplateFamilyProposal({
    state: stateWithReceipt,
    expectedAuthoringRevision: stateWithReceipt.authoringRevision,
    proposalId: input.proposalId,
    edit: input.edit,
    createdBy: "model",
    modelInvocationReceiptId: input.receipt.receiptId,
    reason: input.reason,
    createdAt: input.createdAt,
  });
  const atomic = {
    ...staged,
    authoringRevision: input.state.authoringRevision + 1,
  };
  await assertStateIntegrity(atomic);
  return atomic;
}

export async function rejectCogoDocumentTemplateFamilyProposal(input: {
  state: CogoDocumentTemplateFamilyAuthoringState;
  expectedAuthoringRevision: number;
  proposalId: string;
  rejectedByRef: string;
  rejectedAt: string;
}): Promise<CogoDocumentTemplateFamilyAuthoringState> {
  assertExpectedRevision(input.state, input.expectedAuthoringRevision);
  await assertStateIntegrity(input.state);
  assertNonEmpty(input.rejectedByRef, "rejectedByRef");
  assertIsoDate(input.rejectedAt, "rejectedAt");
  const proposal = proposalForState(input.state, input.proposalId);
  if (proposal.status !== "staged") {
    throw new Error(
      `Proposal ${input.proposalId} is already ${proposal.status}.`,
    );
  }
  const { proposalHash: _proposalHash, ...proposalWithoutHash } = proposal;
  const rejectedBasis: Omit<
    CogoDocumentTemplateFamilyProposal,
    "proposalHash"
  > = {
    ...proposalWithoutHash,
    status: "rejected",
    decidedAt: input.rejectedAt,
    decidedByRef: input.rejectedByRef.trim(),
  };
  const rejected: CogoDocumentTemplateFamilyProposal = {
    ...rejectedBasis,
    proposalHash: await sha256CanonicalJson(proposalHashBasis(rejectedBasis)),
  };
  return {
    ...input.state,
    authoringRevision: input.state.authoringRevision + 1,
    proposals: updateProposal(input.state.proposals, rejected),
    updatedAt: input.rejectedAt,
  };
}

export async function applyCogoDocumentTemplateFamilyProposal(input: {
  state: CogoDocumentTemplateFamilyAuthoringState;
  expectedAuthoringRevision: number;
  proposalId: string;
  approvedByRef: string;
  humanApproved: boolean;
  appliedAt: string;
}): Promise<CogoDocumentTemplateFamilyAuthoringState> {
  assertExpectedRevision(input.state, input.expectedAuthoringRevision);
  await assertStateIntegrity(input.state);
  if (!input.humanApproved) {
    throw new Error(
      "Applying a template family proposal requires explicit human approval.",
    );
  }
  assertNonEmpty(input.approvedByRef, "approvedByRef");
  assertIsoDate(input.appliedAt, "appliedAt");
  const proposal = proposalForState(input.state, input.proposalId);
  if (proposal.status !== "staged") {
    throw new Error(
      `Proposal ${input.proposalId} is already ${proposal.status}.`,
    );
  }
  if (
    proposal.baseFamilyRevision !== input.state.familyRevision ||
    proposal.baseFamilyHash !== input.state.familyHash
  ) {
    throw new DocumentTemplateFamilyIntegrityError(
      `Proposal ${proposal.proposalId} targets family revision ${proposal.baseFamilyRevision}, current ${input.state.familyRevision}.`,
    );
  }
  const { proposalHash: _proposalHash, ...proposalBasis } = proposal;
  const observedProposalHash = await sha256CanonicalJson(
    proposalHashBasis(proposalBasis),
  );
  if (observedProposalHash !== proposal.proposalHash) {
    throw new DocumentTemplateFamilyIntegrityError(
      `Proposal ${proposal.proposalId} hash does not match its typed edit.`,
    );
  }
  const family = applyFamilyEdit(input.state.family, proposal.edit);
  if (input.state.lifecycle === "promoted") {
    if (family.familyVersion === input.state.family.familyVersion) {
      family.familyVersion = nextPatchVersion(family.familyVersion);
    } else if (
      compareSemanticVersions(
        family.familyVersion,
        input.state.family.familyVersion,
      ) <= 0
    ) {
      throw new Error(
        "Editing a promoted family requires a version greater than the promoted version.",
      );
    }
  }
  assertFamilyAuthority(family);
  const familyHash = await sha256CanonicalJson(family);
  const appliedBasis: Omit<CogoDocumentTemplateFamilyProposal, "proposalHash"> =
    {
      ...proposalBasis,
      status: "applied",
      decidedAt: input.appliedAt,
      decidedByRef: input.approvedByRef.trim(),
      humanApproved: true,
    };
  const applied: CogoDocumentTemplateFamilyProposal = {
    ...appliedBasis,
    proposalHash: await sha256CanonicalJson(proposalHashBasis(appliedBasis)),
  };
  if (familyHash === input.state.familyHash) {
    return {
      ...input.state,
      authoringRevision: input.state.authoringRevision + 1,
      proposals: updateProposal(input.state.proposals, applied),
      updatedAt: input.appliedAt,
    };
  }
  return {
    ...input.state,
    authoringRevision: input.state.authoringRevision + 1,
    lifecycle: "draft",
    familyRevision: input.state.familyRevision + 1,
    familyHash,
    family,
    proposals: updateProposal(input.state.proposals, applied),
    admission: null,
    compileReceipts: [],
    proofs: [],
    promotionReceipt: null,
    updatedAt: input.appliedAt,
  };
}

export async function admitCogoDocumentTemplateFamilyAuthoringState(input: {
  state: CogoDocumentTemplateFamilyAuthoringState;
  expectedAuthoringRevision: number;
  checkedAt: string;
}): Promise<CogoDocumentTemplateFamilyAuthoringState> {
  assertExpectedRevision(input.state, input.expectedAuthoringRevision);
  assertIsoDate(input.checkedAt, "checkedAt");
  await assertStateIntegrity(input.state);
  const result = validateDocumentTemplateFamilyAdmission(input.state.family);
  const admission: CogoDocumentTemplateFamilyAdmissionSnapshot = {
    familyRevision: input.state.familyRevision,
    familyHash: input.state.familyHash,
    checkedAt: input.checkedAt,
    result: structuredClone(result),
  };
  return {
    ...input.state,
    authoringRevision: input.state.authoringRevision + 1,
    lifecycle: result.admitted
      ? input.state.lifecycle === "promoted"
        ? "promoted"
        : "candidate"
      : "draft",
    admission,
    proofs: result.admitted ? input.state.proofs : [],
    promotionReceipt: result.admitted ? input.state.promotionReceipt : null,
    updatedAt: input.checkedAt,
  };
}

function currentAdmission(
  state: CogoDocumentTemplateFamilyAuthoringState,
): CogoDocumentTemplateFamilyAdmissionSnapshot {
  const admission = state.admission;
  if (
    !admission?.result.admitted ||
    admission.familyRevision !== state.familyRevision ||
    admission.familyHash !== state.familyHash
  ) {
    throw new Error(
      "Document template family requires combined admission for the current revision before proof or promotion.",
    );
  }
  return admission;
}

function declaredVariantTarget(
  family: CogoDocumentTemplateFamily,
  variantId: string,
  target: CogoDocumentTemplateFamilyTarget,
): boolean {
  const variant = family.variants.find(
    (candidate) => candidate.variantId === variantId,
  );
  return Boolean(
    variant &&
    (
      [...variant.renderTargets, ...variant.publicationTargets] as string[]
    ).includes(target),
  );
}

function targetProofRequiresVisualDiffEvidence(
  family: CogoDocumentTemplateFamily,
  variantId: string,
): boolean {
  const variant = family.variants.find(
    (candidate) => candidate.variantId === variantId,
  );
  if (!variant) {
    throw new DocumentTemplateFamilyIntegrityError(
      `Target proof references unknown variant ${variantId}.`,
    );
  }
  return (
    family.semanticCore.proofProfile.visualDiffPolicy === "always" ||
    variant.layoutProfile !== "block-page"
  );
}

const TARGET_CONTENT_TYPES: Record<CogoDocumentTemplateFamilyTarget, string> = {
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  hwpx: "application/hwp+zip",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pdf: "application/pdf",
  "google-docs": "application/vnd.google-apps.document",
  notion: "application/vnd.notion.page+json",
};

export async function recordCogoDocumentTemplateFamilyCompileReceipt(input: {
  state: CogoDocumentTemplateFamilyAuthoringState;
  expectedAuthoringRevision: number;
  receipt: CogoDocumentTemplateFamilyCompileReceipt;
}): Promise<CogoDocumentTemplateFamilyAuthoringState> {
  assertExpectedRevision(input.state, input.expectedAuthoringRevision);
  await assertStateIntegrity(input.state);
  if (input.state.source.kind !== "catalog-clone") {
    throw new DocumentTemplateFamilyIntegrityError(
      "Blank-family compilation is not available until the general flow-page compiler lands.",
    );
  }
  const receipt = structuredClone(input.receipt);
  const { receiptHash, ...receiptBasis } = receipt;
  if (
    (await hashCogoDocumentTemplateFamilyCompileReceipt(receiptBasis)) !==
      receiptHash ||
    receipt.familyId !== input.state.family.familyId ||
    receipt.familyVersion !== input.state.family.familyVersion ||
    receipt.familyRevision !== input.state.familyRevision ||
    receipt.familyHash !== input.state.familyHash ||
    receipt.variantId !== input.state.selectedVariantId ||
    !input.state.family.variants.some(
      (variant) => variant.variantId === receipt.variantId,
    ) ||
    receipt.requiredTargets.length === 0 ||
    new Set(receipt.requiredTargets).size !== receipt.requiredTargets.length ||
    receipt.nativeArtifactStatus !== "pending-native-render" ||
    receipt.modelFinalWriterAllowed !== false
  ) {
    throw new DocumentTemplateFamilyIntegrityError(
      "Compile receipt failed immutable hash or current family, variant, IR, and target binding verification.",
    );
  }
  assertIsoDate(receipt.compiledAt, "compiledAt");
  assertSha256(receipt.generationIrHash, "generationIrHash");
  assertNonEmpty(receipt.sourceRef, "sourceRef");
  assertSha256(receipt.sourceHash, "sourceHash");
  const variant = input.state.family.variants.find(
    (candidate) => candidate.variantId === receipt.variantId,
  )!;
  const declaredTargets = new Set<string>([
    ...variant.renderTargets,
    ...variant.publicationTargets,
  ]);
  if (receipt.requiredTargets.some((target) => !declaredTargets.has(target))) {
    throw new DocumentTemplateFamilyIntegrityError(
      "Compile receipt contains a target not declared by the current variant.",
    );
  }
  if (
    input.state.compileReceipts.some(
      (candidate) => candidate.receiptHash === receipt.receiptHash,
    )
  ) {
    throw new Error(`Duplicate compile receipt: ${receipt.receiptHash}`);
  }
  return {
    ...input.state,
    authoringRevision: input.state.authoringRevision + 1,
    compileReceipts: [...input.state.compileReceipts, receipt],
    updatedAt: receipt.compiledAt,
  };
}

export async function registerCogoDocumentTemplateFamilyTargetProof(input: {
  state: CogoDocumentTemplateFamilyAuthoringState;
  expectedAuthoringRevision: number;
  verificationReceipt: CogoDocumentTemplateFamilyTargetVerificationReceipt;
  verifyTrustedReceipt: CogoDocumentTemplateFamilyTrustedTargetReceiptVerifier;
  proofId?: string;
  recordedByRef: string;
  recordedAt: string;
}): Promise<CogoDocumentTemplateFamilyAuthoringState> {
  assertExpectedRevision(input.state, input.expectedAuthoringRevision);
  await assertStateIntegrity(input.state);
  currentAdmission(input.state);
  if (input.state.lifecycle !== "candidate") {
    throw new Error(
      "Target proof can only be registered for an admitted candidate.",
    );
  }
  const receipt = structuredClone(input.verificationReceipt);
  const { receiptHash, ...receiptBasis } = receipt;
  if (
    (await hashCogoDocumentTemplateFamilyTargetVerificationReceipt(
      receiptBasis,
    )) !== receiptHash
  ) {
    throw new DocumentTemplateFamilyIntegrityError(
      "Trusted target verification receipt does not match its immutable receipt hash.",
    );
  }
  if (
    receipt.familyId !== input.state.family.familyId ||
    receipt.familyVersion !== input.state.family.familyVersion ||
    receipt.familyRevision !== input.state.familyRevision ||
    receipt.familyHash !== input.state.familyHash
  ) {
    throw new DocumentTemplateFamilyIntegrityError(
      "Trusted target verification receipt does not match the current family revision.",
    );
  }
  if (
    !declaredVariantTarget(
      input.state.family,
      receipt.variantId,
      receipt.target,
    )
  ) {
    throw new Error(
      `Target ${receipt.target} is not declared by variant ${receipt.variantId}.`,
    );
  }
  const compileReceipt = input.state.compileReceipts.find(
    (candidate) => candidate.receiptHash === receipt.compileReceiptHash,
  );
  if (
    !compileReceipt ||
    compileReceipt.familyRevision !== receipt.familyRevision ||
    compileReceipt.familyHash !== receipt.familyHash ||
    compileReceipt.variantId !== receipt.variantId ||
    compileReceipt.generationIrHash !== receipt.generationIrHash ||
    !compileReceipt.requiredTargets.includes(receipt.target)
  ) {
    throw new DocumentTemplateFamilyIntegrityError(
      "Trusted target verification receipt is not bound to an exact persisted compile receipt.",
    );
  }
  const expectedContentType = TARGET_CONTENT_TYPES[receipt.target];
  if (
    receipt.targetContentType !== expectedContentType ||
    receipt.observedContentType !== expectedContentType
  ) {
    throw new Error(
      `Target ${receipt.target} proof must bind artifact and openability readback to ${expectedContentType}.`,
    );
  }
  const providerTarget =
    receipt.target === "google-docs" || receipt.target === "notion";
  if (
    (providerTarget &&
      receipt.verificationAuthority !== "connected-document-provider") ||
    (!providerTarget && receipt.verificationAuthority !== "cogo-native-writer")
  ) {
    throw new DocumentTemplateFamilyIntegrityError(
      "Verification authority does not match the native or connected-provider target boundary.",
    );
  }
  if (providerTarget) {
    assertNonEmpty(receipt.providerVersion ?? "", "providerVersion");
  }
  if (
    targetProofRequiresVisualDiffEvidence(
      input.state.family,
      receipt.variantId,
    ) &&
    receipt.visualDiffEvidence === undefined
  ) {
    throw new DocumentTemplateFamilyIntegrityError(
      `Target ${receipt.target} proof requires visual diff evidence under the current family proof profile.`,
    );
  }
  if (
    receipt.visualDiffEvidence !== undefined &&
    (!isVisualDiffEvidenceRecord(receipt.visualDiffEvidence) ||
      receipt.visualDiffEvidence.renderArtifactRef === receipt.artifactRef ||
      receipt.visualDiffEvidence.oracleRef === receipt.artifactRef ||
      receipt.visualDiffEvidence.oracleRef ===
        receipt.visualDiffEvidence.renderArtifactRef)
  ) {
    throw new DocumentTemplateFamilyIntegrityError(
      "Trusted target verification receipt contains invalid visual diff evidence.",
    );
  }
  assertNonEmpty(receipt.verificationId, "verificationId");
  assertNonEmpty(receipt.verifierRef, "verifierRef");
  assertIsoDate(receipt.verifiedAt, "verifiedAt");
  assertSha256(receipt.generationIrHash, "generationIrHash");
  assertNonEmpty(receipt.artifactRef, "artifactRef");
  assertSha256(receipt.artifactHash, "artifactHash");
  assertNonEmpty(receipt.openabilityReadbackRef, "openabilityReadbackRef");
  assertSha256(receipt.openabilityReadbackHash, "openabilityReadbackHash");
  assertNonEmpty(receipt.structureReadbackRef, "structureReadbackRef");
  assertSha256(receipt.structureHash, "structureHash");
  if (
    receipt.openabilityReadbackPass !== true ||
    receipt.structureReadbackPass !== true ||
    receipt.canonicalAuthority !== false ||
    receipt.finalWriterAllowed !== false ||
    !(await input.verifyTrustedReceipt(structuredClone(receipt)))
  ) {
    throw new DocumentTemplateFamilyIntegrityError(
      "Trusted verifier rejected the native/provider verification receipt.",
    );
  }
  assertNonEmpty(input.recordedByRef, "recordedByRef");
  assertIsoDate(input.recordedAt, "recordedAt");
  const proofId =
    input.proofId?.trim() ||
    `${input.state.candidateId}.revision-${input.state.familyRevision}.${receipt.variantId}.${receipt.target}`;
  assertNonEmpty(proofId, "proofId");
  const proofBasis: Omit<
    CogoDocumentTemplateFamilyTargetProof,
    "proofRecordHash"
  > = {
    schemaVersion: COGO_DOCUMENT_TEMPLATE_FAMILY_TARGET_PROOF_SCHEMA_VERSION,
    proofId,
    familyRevision: input.state.familyRevision,
    familyHash: input.state.familyHash,
    variantId: receipt.variantId,
    target: receipt.target,
    compileReceiptHash: receipt.compileReceiptHash,
    generationIrHash: receipt.generationIrHash,
    targetContentType: receipt.targetContentType,
    artifactRef: receipt.artifactRef,
    artifactHash: receipt.artifactHash,
    openabilityReadbackRef: receipt.openabilityReadbackRef,
    openabilityReadbackHash: receipt.openabilityReadbackHash,
    observedContentType: receipt.observedContentType,
    structureReadbackRef: receipt.structureReadbackRef,
    structureHash: receipt.structureHash,
    ...(receipt.visualDiffEvidence
      ? { visualDiffEvidence: receipt.visualDiffEvidence }
      : {}),
    ...(receipt.providerVersion
      ? { providerVersion: receipt.providerVersion }
      : {}),
    recordedByRef: input.recordedByRef.trim(),
    recordedAt: input.recordedAt,
    openabilityReadbackPass: true,
    structureReadbackPass: true,
    canonicalAuthority: false,
    finalWriterAllowed: false,
    verificationReceipt: receipt,
  };
  const proof: CogoDocumentTemplateFamilyTargetProof = {
    ...proofBasis,
    proofRecordHash: await sha256CanonicalJson(proofBasis),
  };
  const proofs = input.state.proofs.filter(
    (candidate) =>
      !(
        candidate.familyRevision === proof.familyRevision &&
        candidate.familyHash === proof.familyHash &&
        candidate.variantId === proof.variantId &&
        candidate.target === proof.target
      ),
  );
  return {
    ...input.state,
    authoringRevision: input.state.authoringRevision + 1,
    proofs: [...proofs, proof],
    updatedAt: input.recordedAt,
  };
}

function requiredProofKeys(family: CogoDocumentTemplateFamily): string[] {
  return family.variants.flatMap((variant) =>
    ([...variant.renderTargets, ...variant.publicationTargets] as string[]).map(
      (target) => `${variant.variantId}::${target}`,
    ),
  );
}

function proofKey(proof: CogoDocumentTemplateFamilyTargetProof): string {
  return `${proof.variantId}::${proof.target}`;
}

export async function promoteCogoDocumentTemplateFamily(input: {
  state: CogoDocumentTemplateFamilyAuthoringState;
  expectedAuthoringRevision: number;
  expectedFamilyHash: string;
  verifyTrustedReceipt: CogoDocumentTemplateFamilyTrustedTargetReceiptVerifier;
  reviewerRef: string;
  reviewReason: string;
  humanApproved: boolean;
  promotedAt: string;
}): Promise<CogoDocumentTemplateFamilyAuthoringState> {
  assertExpectedRevision(input.state, input.expectedAuthoringRevision);
  await assertStateIntegrity(input.state);
  if (input.expectedFamilyHash !== input.state.familyHash) {
    throw new DocumentTemplateFamilyIntegrityError(
      "Promotion familyHash does not match the current family revision.",
    );
  }
  const admission = currentAdmission(input.state);
  const observedAdmission = validateDocumentTemplateFamilyAdmission(
    input.state.family,
  );
  if (!observedAdmission.admitted) {
    throw new DocumentTemplateFamilyIntegrityError(
      "Promotion requires combined admission of the current family payload.",
    );
  }
  if (input.state.lifecycle !== "candidate") {
    throw new Error(
      "Only an admitted document template family candidate can be promoted.",
    );
  }
  if (input.state.promotionReceipt) {
    throw new Error(
      "This document template family revision is already promoted.",
    );
  }
  if (!input.humanApproved) {
    throw new Error(
      "Document template family promotion requires explicit human approval.",
    );
  }
  assertNonEmpty(input.reviewerRef, "reviewerRef");
  assertNonEmpty(input.reviewReason, "reviewReason");
  assertIsoDate(input.promotedAt, "promotedAt");
  const currentProofs = input.state.proofs.filter(
    (proof) =>
      proof.familyRevision === input.state.familyRevision &&
      proof.familyHash === input.state.familyHash &&
      proof.openabilityReadbackPass === true &&
      proof.structureReadbackPass === true,
  );
  for (const proof of currentProofs) {
    if (
      targetProofRequiresVisualDiffEvidence(
        input.state.family,
        proof.variantId,
      ) &&
      proof.visualDiffEvidence === undefined
    ) {
      throw new DocumentTemplateFamilyIntegrityError(
        `Target proof ${proof.proofId} requires visual diff evidence under the current family proof profile.`,
      );
    }
    const { proofRecordHash, ...proofBasis } = proof;
    if ((await sha256CanonicalJson(proofBasis)) !== proofRecordHash) {
      throw new DocumentTemplateFamilyIntegrityError(
        `Target proof ${proof.proofId} does not match its immutable record hash.`,
      );
    }
    const { receiptHash, ...verificationBasis } = proof.verificationReceipt;
    if (
      (await hashCogoDocumentTemplateFamilyTargetVerificationReceipt(
        verificationBasis,
      )) !== receiptHash ||
      !(await input.verifyTrustedReceipt(
        structuredClone(proof.verificationReceipt),
      ))
    ) {
      throw new DocumentTemplateFamilyIntegrityError(
        `Trusted verifier rejected target proof ${proof.proofId} during promotion re-verification.`,
      );
    }
    const compileReceipt = input.state.compileReceipts.find(
      (candidate) => candidate.receiptHash === proof.compileReceiptHash,
    );
    if (
      !compileReceipt ||
      compileReceipt.familyRevision !== proof.familyRevision ||
      compileReceipt.familyHash !== proof.familyHash ||
      compileReceipt.variantId !== proof.variantId ||
      compileReceipt.generationIrHash !== proof.generationIrHash ||
      !compileReceipt.requiredTargets.includes(proof.target)
    ) {
      throw new DocumentTemplateFamilyIntegrityError(
        `Target proof ${proof.proofId} lost its exact persisted compile receipt binding.`,
      );
    }
  }
  const provenKeys = new Set(currentProofs.map(proofKey));
  const missingProofs = requiredProofKeys(input.state.family).filter(
    (key) => !provenKeys.has(key),
  );
  if (missingProofs.length > 0) {
    throw new Error(
      `Document template family promotion requires current-revision target readback proof: ${missingProofs.join(", ")}.`,
    );
  }
  for (const variant of input.state.family.variants) {
    const generationHashes = new Set(
      currentProofs
        .filter((proof) => proof.variantId === variant.variantId)
        .map((proof) => proof.generationIrHash),
    );
    if (generationHashes.size !== 1) {
      throw new Error(
        `Variant ${variant.variantId} target proofs must share one generation IR hash.`,
      );
    }
  }
  const admissionSnapshotHash = await sha256CanonicalJson(admission);
  const proofBindings = currentProofs
    .map(({ proofId, proofRecordHash }) => ({ proofId, proofRecordHash }))
    .sort((left, right) => left.proofId.localeCompare(right.proofId));
  if (
    new Set(proofBindings.map((binding) => binding.proofId)).size !==
    proofBindings.length
  ) {
    throw new Error("Target proof IDs must be unique before promotion.");
  }
  const receiptBasis = {
    schemaVersion: COGO_DOCUMENT_TEMPLATE_FAMILY_PROMOTION_SCHEMA_VERSION,
    promotionId: `${input.state.candidateId}.revision-${input.state.familyRevision}.promotion`,
    promotedAt: input.promotedAt,
    familyId: input.state.family.familyId,
    familyVersion: input.state.family.familyVersion,
    familyRevision: input.state.familyRevision,
    familyHash: input.state.familyHash,
    proofBindings,
    admissionSnapshotHash,
    reviewerRef: input.reviewerRef.trim(),
    reviewReason: input.reviewReason.trim(),
    humanApproved: true as const,
    automatedPromotion: false as const,
    modelCanonicalAuthority: false as const,
    modelFinalWriterAllowed: false as const,
  };
  const promotionReceipt: CogoDocumentTemplateFamilyPromotionReceipt = {
    ...receiptBasis,
    receiptHash: await sha256CanonicalJson(receiptBasis),
  };
  return {
    ...input.state,
    authoringRevision: input.state.authoringRevision + 1,
    lifecycle: "promoted",
    promotionReceipt,
    updatedAt: input.promotedAt,
  };
}
